"use client";
import { getModel } from "@/lib/api/generate";
import { getMasterTag, getTags } from "@/lib/api/tag";
import React, { createContext, useContext, useReducer, ReactNode } from "react";
import { StringLiteral } from "typescript";

export interface Tag {
  id: string;
  name: string;
  selected: boolean;
  savedTag?: boolean;
  masterTag?: string;
}

export interface TagGroup {
  id: string;
  name: string;
  type: string; // Could be 'single', 'multiple', etc.
  tags: Tag[];
  tagIds?: string[];
}

interface CreditCost {
  generatorCost: number;
  premiumCost: number;
  upscaleCost: number;
  freeTagLimit: number;
  tagCost: number;
  characterCost: number;
  cfgScaleCost: number;
  aspectRatioCost: number;
  promptLimit: number;
  promptCost: number;
}

// Define the shape of your state
export interface GeneratorState {
  selectedGenerator: {
    id: string;
    styleName: Array<{
      name: string;
      value: string;
    }>;
    styleImage: any;
    cfgU: number;
    cfgM: number;
    cfgL: number;
  };
  selectedStyleUgc: {
    name: string;
    tagGroups: any[];
  };
  selectedMasterUgc: {
    id: string;
    style: string;
    tag: { names?: any };
  };
  selectedStyleGroupsUgc: string[];
  selectedStyleCreateUgc: {
    name: string;
    _id: string;
    tagGroups: any[];
  };
  selectedStyleGroupCreateUgc: any;
  positivePromptCreateUgc: string;
  negativePromptCreateUgc: string;
  nameCreateUgc: string;
  creditCost: CreditCost;
  currentQueueType: string;
  queueType: string;
  freeQueueDelay: Date;
  tagList: TagGroup[];
  customPrompt: string;
  customNegative: string;
  cfgScale: number;
  resolution: number;
  denoisingStrength: number;
  seed: number;
  generatedImage: string;
  imageLoadingState: number;
  queuePosition: string;
  saveState: number;
  upscaleState: number;
  detectCelebrity: boolean;
  // Add other properties as needed
}

// Default initial state
const initialState: GeneratorState = {
  selectedGenerator: {
    id: "",
    styleName: [], // Corrected to an empty array
    styleImage: "",
    cfgU: 0.0,
    cfgM: 0.0,
    cfgL: 0.0,
  },
  selectedStyleUgc: { name: "", tagGroups: [] },
  selectedStyleGroupsUgc: ["all"],
  selectedMasterUgc: { id: "", style: "", tag: {} },
  selectedStyleCreateUgc: { name: "", _id: "", tagGroups: [] },
  selectedStyleGroupCreateUgc: { _id: "", names: [] },
  positivePromptCreateUgc: "",
  negativePromptCreateUgc: "",
  nameCreateUgc: "",
  creditCost: {
    generatorCost: 0,
    premiumCost: 0,
    upscaleCost: 0,
    freeTagLimit: 0,
    tagCost: 0,
    characterCost: 0,
    cfgScaleCost: 0,
    aspectRatioCost: 0,
    promptLimit: 0,
    promptCost: 0,
  },
  currentQueueType: "free",
  queueType: "free",
  freeQueueDelay: new Date("January 1, 1970 00:00:00 UTC"),
  tagList: [],
  customPrompt: "",
  customNegative: "",
  cfgScale: 0.0,
  resolution: 1,
  denoisingStrength: 0,
  seed: 0,
  saveState: 0,
  upscaleState: 0,
  generatedImage: "",
  imageLoadingState: 0,
  queuePosition: "",
  detectCelebrity: false,
};

interface GeneratorContextProviderProps {
  children: ReactNode;
}

// Expanded action types
type GeneratorAction =
  | {
      type: "SET_SELECTED_GENERATOR";
      payload: GeneratorState["selectedGenerator"];
    }
  | { type: "SET_SELECTED_STYLE_UGC"; payload: any }
  | { type: "SET_SELECTED_STYLE_GROUPS_UGC"; payload: any }
  | { type: "SET_SELECTED_MASTER_UGC"; payload: any }
  | { type: "SET_SELECTED_STYLE_CREATE_UGC"; payload: any }
  | { type: "SET_SELECTED_STYLE_GROUP_CREATE_UGC"; payload: any }
  | { type: "SET_POSITIVE_PROMPT_CREATE_UGC"; payload: any }
  | { type: "SET_NEGATIVE_PROMPT_CREATE_UGC"; payload: any }
  | { type: "SET_NAME_CREATE_UGC"; payload: any }
  | { type: "SET_CREDIT_COST"; payload: CreditCost }
  | { type: "SET_CURRENT_QUEUE_TYPE"; payload: string }
  | { type: "SET_QUEUE_TYPE"; payload: string }
  | { type: "SET_FREE_QUEUE_DELAY"; payload: Date }
  | { type: "SET_TAG_LIST"; payload: TagGroup[] }
  | { type: "SET_CUSTOM_PROMPT"; payload: string }
  | { type: "SET_CUSTOM_NEGATIVE_PROMPT"; payload: string }
  | { type: "SET_CFG_SCALE"; payload: number }
  | { type: "SET_RESOLUTION"; payload: number }
  | { type: "SET_DENOISING_STRENGTH"; payload: number }
  | { type: "SET_SEED"; payload: number }
  | { type: "RESET_TAGS" }
  | { type: "SET_IMAGE_LOADING_STATE"; payload: number }
  | { type: "SET_GENERATED_IMAGE"; payload: string }
  | { type: "SET_QUEUE_POSITION"; payload: string }
  | { type: "SET_SAVE_STATE"; payload: number }
  | { type: "SET_UPSCALE_STATE"; payload: number }
  | { type: "SET_DETECT_CELEBRITY"; payload: boolean };
// Add other action types as needed

// Expanded reducer function
function generatorReducer(state: GeneratorState, action: GeneratorAction): GeneratorState {
  switch (action.type) {
    case "SET_SELECTED_GENERATOR":
      return { ...state, selectedGenerator: action.payload };
    case "SET_SELECTED_STYLE_UGC":
      return { ...state, selectedStyleUgc: action.payload };
    case "SET_SELECTED_STYLE_GROUPS_UGC":
      return { ...state, selectedStyleGroupsUgc: action.payload };
    case "SET_SELECTED_MASTER_UGC":
      return { ...state, selectedMasterUgc: action.payload };
    case "SET_SELECTED_STYLE_CREATE_UGC":
      return { ...state, selectedStyleCreateUgc: action.payload };
    case "SET_SELECTED_STYLE_GROUP_CREATE_UGC":
      return { ...state, selectedStyleGroupCreateUgc: action.payload };
    case "SET_POSITIVE_PROMPT_CREATE_UGC":
      return { ...state, positivePromptCreateUgc: action.payload };
    case "SET_NEGATIVE_PROMPT_CREATE_UGC":
      return { ...state, negativePromptCreateUgc: action.payload };
    case "SET_NAME_CREATE_UGC":
      return { ...state, nameCreateUgc: action.payload };
    case "SET_CREDIT_COST":
      return { ...state, creditCost: action.payload };
    case "SET_CURRENT_QUEUE_TYPE":
      return { ...state, currentQueueType: action.payload };
    case "SET_QUEUE_TYPE":
      return { ...state, queueType: action.payload };
    case "SET_FREE_QUEUE_DELAY":
      return { ...state, freeQueueDelay: action.payload };
    case "SET_TAG_LIST":
      return { ...state, tagList: action.payload };
    case "SET_CUSTOM_PROMPT":
      return { ...state, customPrompt: action.payload };
    case "SET_CUSTOM_NEGATIVE_PROMPT":
      return { ...state, customNegative: action.payload };
    case "SET_CFG_SCALE":
      return { ...state, cfgScale: action.payload };
    case "SET_RESOLUTION":
      return { ...state, resolution: action.payload };
    case "SET_DENOISING_STRENGTH":
      return { ...state, denoisingStrength: action.payload };
    case "SET_SEED":
      return { ...state, seed: action.payload };
    case "SET_IMAGE_LOADING_STATE":
      return { ...state, imageLoadingState: action.payload };
    case "SET_GENERATED_IMAGE":
      return { ...state, generatedImage: action.payload };
    case "SET_QUEUE_POSITION":
      return { ...state, queuePosition: action.payload };
    case "SET_SAVE_STATE":
      return { ...state, saveState: action.payload };
    case "SET_UPSCALE_STATE":
      return { ...state, upscaleState: action.payload };
    case "SET_DETECT_CELEBRITY":
      return { ...state, detectCelebrity: action.payload };
    default:
      return state;
  }
}

// Context creation with specific type
const GeneratorContext = createContext<{
  state: GeneratorState;
  dispatch: React.Dispatch<GeneratorAction>;
}>({
  state: initialState,
  dispatch: () => {},
});

// Provider component
export const GeneratorContextProvider: React.FC<GeneratorContextProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(generatorReducer, initialState);

  // shared api calls can go here, that will call dispatch and modify state

  return (
    <GeneratorContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </GeneratorContext.Provider>
  );
};

// Custom hook for using context
export const useGeneratorContext = () => useContext(GeneratorContext);
